import {
  DeletePaymentSessionResponse,
  GetPaymentSessionResponse,
  PostPaymentSession,
  PostPaymentSessionResponse
} from '@/hooks/payment/types';
import { FetchError, useApi } from '@/hooks/useApi';
import { useConfig } from '@/hooks/useConfig';
import { formatPriceString, formatStringToAmountInCents } from '@/utils/numbers';
import { useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { PartialPayment } from './types';
import { PaymentSessionContext } from '@/hooks/payment/usePaymentSession';
import { failedPaymentStatuses, successfulPaymentStatuses } from '@/utils/constants';

export type PaymentSessionContextType = {
  isLoading: boolean;
  isFetching: boolean;
  paymentSession: GetPaymentSessionResponse | undefined;
  postPaymentSession: UseMutationResult<PostPaymentSessionResponse, FetchError, PostPaymentSession, unknown>;
  cancelPaymentSession: UseMutationResult<DeletePaymentSessionResponse, FetchError, string, unknown>;
  partialPayment: PartialPayment;
  setPaymentSessionId: (id: string | undefined) => void;
  resetPaymentSession: () => void;
  queryError: FetchError | null;
};

export const PaymentSessionProvider = ({ children }: { children: React.ReactNode }) => {
  const { urls } = useConfig();
  const apiFetch = useApi();
  const queryClient = useQueryClient();
  const [partialPaymentEnabled, setPartialPaymentEnabled] = useState(false);
  const [partialPaymentAmount, setPartialPaymentAmount] = useState<string>('');
  const [paymentSessionId, setPaymentSessionId] = useState<string | undefined>(undefined);

  const {
    isLoading,
    isFetching,
    data: paymentSession,
    error: queryError
  } = useQuery<GetPaymentSessionResponse, FetchError>({
    queryKey: ['getPaymentSession', paymentSessionId, urls.akovaPay],
    queryFn: async () => {
      const result = await apiFetch<GetPaymentSessionResponse>(
        `${urls?.akovaPay}/v1/paymentsession/${paymentSessionId}`
      );
      return result;
    },
    refetchInterval: (data) => {
      if (
        (data.state.data && successfulPaymentStatuses.includes(data.state.data.payment.status?.toLowerCase())) ||
        (data.state.data && failedPaymentStatuses.includes(data.state.data.payment.status?.toLowerCase()))
      ) {
        return false;
      }

      return 5000;
    },
    enabled: !!paymentSessionId
  });

  const handleResetPaymentSession = () => {
    setPaymentSessionId(undefined);
    queryClient.invalidateQueries({ queryKey: ['getPaymentSession', paymentSessionId, urls.akovaPay] });
  };

  const postPaymentSession = useMutation<PostPaymentSessionResponse, FetchError, PostPaymentSession, unknown>({
    mutationFn: ({
      referenceCode,
      deviceType,
      amount,
      paymentMethodId,
      postPaymentRedirectionUrl,
      postPaymentRedirectionFailedUrl,
      postPaymentRedirectionCancelUrl
    }: PostPaymentSession) => {
      return apiFetch<PostPaymentSessionResponse>(`${urls?.akovaPay}/v1/paymentsession`, {
        method: 'POST',
        body: {
          referenceCode,
          deviceType,
          amount,
          paymentMethodId,
          postPaymentRedirectionUrl,
          postPaymentRedirectionFailedUrl,
          postPaymentRedirectionCancelUrl
        }
      });
    }
  });

  const cancelPaymentSession = useMutation<DeletePaymentSessionResponse, FetchError, string, unknown>({
    mutationFn: (paymentSessionId: string) => {
      return apiFetch<DeletePaymentSessionResponse>(`${urls?.akovaPay}/v1/paymentsession/${paymentSessionId}`, {
        method: 'DELETE',
        body: {
          paymentSessionId
        }
      });
    }
  });

  const partialPayment = {
    enabled: partialPaymentEnabled,
    amount: partialPaymentAmount,
    amountNumberInCents: formatStringToAmountInCents(partialPaymentAmount),
    amountPrice: formatPriceString(formatStringToAmountInCents(partialPaymentAmount)),
    setEnabled: setPartialPaymentEnabled,
    setAmount: setPartialPaymentAmount
  };

  return (
    <PaymentSessionContext.Provider
      value={{
        isLoading,
        isFetching,
        paymentSession,
        postPaymentSession,
        cancelPaymentSession,
        partialPayment,
        setPaymentSessionId,
        resetPaymentSession: handleResetPaymentSession,
        queryError
      }}
    >
      {children}
    </PaymentSessionContext.Provider>
  );
};
